import {findToolPart, patchToolPartOutput} from './toolPartSelectors';
import {CAMPAIGN_WIDGET_TOOL_NAMES} from '../utils/campaignCompletionMessages';
import {buildCampaignTypeCompletionMessage} from '../utils/systemChatMessages';
import {buildParameterizationCompletionMessage} from '../utils/parameterizationMessages';
import {
  buildPostQuantityCompletionMessage,
  buildImagesCompletionMessage,
  buildStrategyApprovalCompletionMessage,
  buildArtsApprovalCompletionMessage,
  buildCompletionModeMessage,
  buildSocialNetworksCompletionMessage,
  buildScheduleConfigCompletionMessage,
  buildBatchEditConfirmMessage,
  buildBatchRejectConfirmMessage,
  buildFinalActionCompletionMessage,
} from '../utils/campaignCompletionMessages';

export const PALETTE_UI_TOOL_NAME = 'solicitar_paleta';
export const LOGO_UI_TOOL_NAME = 'solicitar_logo';
export const CAMPAIGN_TYPE_UI_TOOL_NAME = 'solicitar_tipo_campanha';
export const PARAMETERIZATION_TOOL_NAMES = [PALETTE_UI_TOOL_NAME];
export const CAMPAIGN_TYPE_VALUES = ['PRODUCT', 'SERVICE', 'NONE'];

export const CAMPAIGN_TOOL_CATALOG = [
  {
    toolName: PALETTE_UI_TOOL_NAME,
    kind: 'interaction',
    integrationKey: 'palette',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: () => buildParameterizationCompletionMessage('paleta'),
  },
  {
    toolName: LOGO_UI_TOOL_NAME,
    kind: 'interaction',
    integrationKey: 'logoUpload',
    blocksTextInput: false,
    attachMode: 'logo',
    completionBuilder: null,
  },
  {
    toolName: CAMPAIGN_TYPE_UI_TOOL_NAME,
    kind: 'interaction',
    integrationKey: 'campaignType',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildCampaignTypeCompletionMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.QUANTIDADE_ARTES,
    kind: 'interaction',
    integrationKey: 'postQuantity',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildPostQuantityCompletionMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.IMAGENS_CAMPANHA,
    kind: 'interaction',
    integrationKey: 'productImages',
    blocksTextInput: false,
    attachMode: 'images',
    completionBuilder: buildImagesCompletionMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA,
    kind: 'interaction',
    integrationKey: 'strategyApproval',
    blocksTextInput: false,
    attachMode: null,
    completionBuilder: buildStrategyApprovalCompletionMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ARTES,
    kind: 'interaction',
    integrationKey: 'artsApproval',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildArtsApprovalCompletionMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.MODO_CONCLUSAO,
    kind: 'interaction',
    integrationKey: 'completionMode',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildCompletionModeMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.REDES_SOCIAIS,
    kind: 'interaction',
    integrationKey: 'socialMedia',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildSocialNetworksCompletionMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.CONFIGURAR_AGENDAMENTO,
    kind: 'interaction',
    integrationKey: 'scheduleConfig',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildScheduleConfigCompletionMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.CONFIRMAR_EDICAO_LOTE,
    kind: 'interaction',
    integrationKey: 'batchEditConfirm',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildBatchEditConfirmMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.CONFIRMAR_REJEICAO_LOTE,
    kind: 'interaction',
    integrationKey: 'batchRejectConfirm',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildBatchRejectConfirmMessage,
  },
  {
    toolName: CAMPAIGN_WIDGET_TOOL_NAMES.ACOES_FINAIS,
    kind: 'interaction',
    integrationKey: 'completionActions',
    blocksTextInput: true,
    attachMode: null,
    completionBuilder: buildFinalActionCompletionMessage,
  },
];

const WIDGET_TOOL_NAMES = new Set(Object.values(CAMPAIGN_WIDGET_TOOL_NAMES));

export const WIDGET_INPUT_LOCK_TOOL_NAMES = CAMPAIGN_TOOL_CATALOG.filter(
  (entry) => WIDGET_TOOL_NAMES.has(entry.toolName) && entry.blocksTextInput,
).map((entry) => entry.toolName);

export function getToolCatalogEntry(toolName) {
  return CAMPAIGN_TOOL_CATALOG.find((entry) => entry.toolName === toolName);
}

export function isToolInputLocked(toolName) {
  return Boolean(getToolCatalogEntry(toolName)?.blocksTextInput);
}

export function getToolAttachMode(toolName) {
  return getToolCatalogEntry(toolName)?.attachMode ?? null;
}

export function getToolTrackingId(part) {
  return part?.output?.trackingId || part?.toolCallId || null;
}

function createCatalogToolHelpers(toolName) {
  return {
    toolName,
    isToolPartType(partType) {
      return partType === `tool-${toolName}`;
    },
    findPart(messages, statuses = null) {
      return findToolPart(messages, toolName, statuses);
    },
    getTrackingId: getToolTrackingId,
  };
}

export const postQuantityWidget = createCatalogToolHelpers(
  CAMPAIGN_WIDGET_TOOL_NAMES.QUANTIDADE_ARTES,
);
export const productImagesWidget = createCatalogToolHelpers(
  CAMPAIGN_WIDGET_TOOL_NAMES.IMAGENS_CAMPANHA,
);
export const logoWidget = {
  toolName: LOGO_UI_TOOL_NAME,
  findPart(messages, statuses = null) {
    return findToolPart(messages, LOGO_UI_TOOL_NAME, statuses);
  },
  getTrackingId: getToolTrackingId,
};

export function isCampaignTypeToolPartType(partType) {
  return partType === `tool-${CAMPAIGN_TYPE_UI_TOOL_NAME}`;
}

export function isParameterizationToolPartType(partType) {
  if (!partType) {
    return false;
  }
  const name = partType.replace(/^tool-/, '');
  return PARAMETERIZATION_TOOL_NAMES.includes(name);
}

export function isWidgetToolPartType(partType) {
  if (!partType?.startsWith('tool-')) {
    return false;
  }
  return WIDGET_TOOL_NAMES.has(partType.slice(5));
}

export function isCatalogToolPartType(partType) {
  if (!partType?.startsWith('tool-')) {
    return false;
  }
  return Boolean(getToolCatalogEntry(partType.slice(5)));
}

export function findCampaignTypeToolPart(messages, statuses = null) {
  return findToolPart(messages, CAMPAIGN_TYPE_UI_TOOL_NAME, statuses);
}

export function getCampaignTypeToolTrackingId(part) {
  return getToolTrackingId(part);
}

export function patchCatalogToolOutput(
  messages,
  toolName,
  trackingId,
  completedData,
) {
  return patchToolPartOutput(messages, toolName, trackingId, completedData);
}

export {buildCampaignTypeCompletionMessage};
